import React from "react";
import type { DestinationCardProps } from "../../types/destination-types";
import { getCategoryConfig } from "../../types/destination-types";

export function DestinationCard({
  destination,
  onEdit,
  onDelete,
  onView,
  showAnalytics = true,
  compact = false,
  deleting = false,
}: DestinationCardProps) {
  const categoryConfig = getCategoryConfig(destination.category);

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const formatUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname + (urlObj.pathname !== "/" ? urlObj.pathname : "");
    } catch {
      return url;
    }
  };

  const handleCardClick = () => {
    if (onView) {
      onView(destination);
    }
  };

  return (
    <div
      className={`bg-white rounded-lg shadow border hover:shadow-md transition-shadow ${
        compact ? "p-4" : "p-6"
      } ${onView ? "cursor-pointer" : ""}`}
      onClick={handleCardClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center flex-1 min-w-0">
          <div
            className={`flex-shrink-0 w-10 h-10 rounded-lg ${categoryConfig.bgColor} flex items-center justify-center mr-3`}
          >
            <svg
              className={`w-5 h-5 ${categoryConfig.textColor}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {destination.name}
            </h3>
            <p className="text-sm text-gray-500 truncate">
              {formatUrl(destination.url)}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2 ml-4">
          {onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(destination);
              }}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Edit destination"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!deleting) {
                  onDelete(destination);
                }
              }}
              disabled={deleting}
              className={`p-2 rounded-lg transition-colors ${
                deleting
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-400 hover:text-red-600 hover:bg-red-50"
              }`}
              title={deleting ? "Deleting..." : "Delete destination"}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Category and Status */}
      <div className="flex items-center justify-between mb-4">
        <span
          className={`px-2 py-1 text-xs font-semibold rounded-full ${categoryConfig.bgColor} ${categoryConfig.textColor}`}
        >
          {categoryConfig.label}
        </span>
        <div className="flex items-center space-x-2">
          {destination.trackingEnabled && (
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
              <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1"></div>
              Tracking
            </span>
          )}
          <span
            className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
              destination.isActive
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {destination.isActive ? "Active" : "Inactive"}
          </span>
        </div>
      </div>

      {/* Description */}
      {destination.description && !compact && (
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
          {destination.description}
        </p>
      )}

      {/* Tags */}
      {destination.tags.length > 0 && !compact && (
        <div className="flex flex-wrap gap-1 mb-4">
          {destination.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
            >
              {tag}
            </span>
          ))}
          {destination.tags.length > 3 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
              +{destination.tags.length - 3} more
            </span>
          )}
        </div>
      )}

      {/* Analytics */}
      {showAnalytics && (
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">
              {formatNumber(destination.analytics.totalClicks)}
            </div>
            <div className="text-xs text-gray-500">Total Clicks</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">
              {formatNumber(destination.analytics.uniqueClicks)}
            </div>
            <div className="text-xs text-gray-500">Unique Clicks</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">
              {destination.analytics.conversionRate
                ? `${destination.analytics.conversionRate.toFixed(1)}%`
                : "N/A"}
            </div>
            <div className="text-xs text-gray-500">Conversion</div>
          </div>
        </div>
      )}

      {/* Conversation Usage */}
      {destination.conversationIds.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center text-sm text-gray-600">
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            Used in {destination.conversationIds.length} conversation
            {destination.conversationIds.length !== 1 ? "s" : ""}
          </div>
        </div>
      )}

      {/* Last Activity */}
      {destination.analytics.lastClickAt && (
        <div className="mt-2">
          <div className="text-xs text-gray-500">
            Last click:{" "}
            {new Date(destination.analytics.lastClickAt).toLocaleDateString()}
          </div>
        </div>
      )}
    </div>
  );
}
